import { mergeTailwindClasses } from '@kontentino/ui';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { useMutation } from 'react-query';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import AiCaptionGeneratorForm from './components/AiCaptionGeneratorForm';
import CaptionGeneratedResults from './components/CaptionGeneratedResults';
import LoadingGeneratedResults from 'app/modules/aiContent/components/results/LoadingGeneratedResults';
import clsx from 'clsx';
import { Icon } from '@kontentino/ui';
import { useSearchParams } from 'react-router-dom';
import { TYPE } from '@kontentino/kontentino-constants/Pages';

export type CaptionFormValues = {
  postText: string;
  postType: string;
  writingStyle: string;
  captionLength: string;
  variationsCount: number;
  language: string | null;
  useEmojis: boolean;
  useHashtags: boolean;
  pageType: number;
  expanded?: boolean;
};

const sharedClassNames = {
  root: 'tw-overflow-y-auto tw-flex-grow',
  img: 'tw-h-[8.5rem]',
  loadingImg: 'tw-h-[8.5rem]',
  emptyImg: 'tw-h-[8.5rem]',
};

const AiCaptionGenerator = () => {
  const [searchParams] = useSearchParams();

  const pageTypeParam = searchParams.get('pageType');
  const pageType = pageTypeParam ? Number(pageTypeParam) : TYPE.FACEBOOK;

  const generateCaptions = useMutation({
    mutationFn: (values: Omit<CaptionFormValues, 'pageType'>) =>
      BrandHubApi.freeToolsGenerateCaptions({
        ...values,
        pageType,
      }),
  });

  const handleSubmit = (values: Omit<CaptionFormValues, 'pageType'>) => {
    if (generateCaptions.isLoading) return;
    generateCaptions.mutate(values);
  };

  const handleReset = () => {
    generateCaptions.reset();
  };

  const hasGeneratedData = !!generateCaptions.data;

  return (
    <div className="tw-mx-auto tw-max-w-4xl">
      <div className="tw-p-6">
        <div className="tw-flex tw-flex-col tw-gap-6">
          <AiCaptionGeneratorForm
            onSubmit={handleSubmit}
            hasGeneratedData={hasGeneratedData}
          >
            {(generateCaptions.data || generateCaptions.isLoading) && (
              <div className="tw-mt-6">
                <div className="tw-mb-4"></div>
                <div
                  className={mergeTailwindClasses(
                    clsx(
                      'tw-flex tw-min-h-[200px] tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg',
                    ),
                  )}
                  data-name="ai-caption-generator-results"
                >
                  {generateCaptions.isLoading ? (
                    <LoadingGeneratedResults
                      title="Generating Captions"
                      classNames={sharedClassNames}
                    />
                  ) : (
                    <CaptionGeneratedResults
                      suggestions={generateCaptions.data?.captions || []}
                      isLoading={false}
                      onBack={handleReset}
                      classNames={sharedClassNames}
                    />
                  )}
                </div>
              </div>
            )}
          </AiCaptionGeneratorForm>

          <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-purple-100/20 tw-bg-gradient-to-br tw-from-primary-50/10 tw-via-purple-50/10 tw-to-purple-50/10 tw-p-6">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 md:tw-flex-row">
              <div className="tw-flex tw-flex-1 tw-items-center tw-gap-4">
                <div className="tw-rounded-full tw-bg-primary-10/40 tw-p-3">
                  <Icon
                    icon={faSparkles}
                    className="tw-h-8 tw-w-8 tw-text-primary-100"
                  />
                </div>
                <p className="tw-text-grayscale-180">
                  Imagine this, but fully in your brand's tone. Try{' '}
                  <a
                    href="https://www.kontentino.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-text-primary-100 tw-underline"
                    data-name="kontentino-link"
                  >
                    Kontentino
                  </a>{' '}
                  to generate captions that match your voice, your vibe, your
                  audience
                </p>
              </div>
              <a
                href="https://www.kontentino.com/signup/"
                target="_blank"
                rel="noopener noreferrer"
                className="tw-inline-flex tw-h-12 tw-items-center tw-justify-center tw-rounded-md tw-bg-gradient-to-r tw-from-purple-100 tw-from-0% tw-to-primary-100 tw-to-100% tw-px-5 tw-py-3 tw-text-md tw-font-medium tw-text-white hover:tw-from-purple-120 hover:tw-to-primary-120"
                data-name="brand-hub-signup-link"
              >
                Try it with your brand
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiCaptionGenerator;
